import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

// Pulse animation for verified badge
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
`;

const VerifiedBadgeContainer = styled(Box)(({ theme, size = 'medium' }) => {
  const sizes = {
    small: 16,
    medium: 20,
    large: 24,
  };
  
  const badgeSize = sizes[size] || sizes.medium;
  
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: badgeSize,
    height: badgeSize,
    position: 'relative',
  };
});

const VerifiedIcon = styled('svg')(({ theme, animate }) => ({
  width: '100%',
  height: '100%',
  animation: animate ? `${pulse} 2s infinite` : 'none',
}));

/**
 * Custom verified badge with original hexagonal shield design
 * Shows a green shield with a checkmark for verified members
 * 
 * @param {string} size - Size variant: 'small' (16px), 'medium' (20px), 'large' (24px)
 * @param {boolean} animate - Whether to show pulse animation
 * @param {string} tooltipText - Custom tooltip text
 * @param {object} sx - Additional Material-UI sx props
 */
const VerifiedBadge = ({ 
  size = 'medium', 
  animate = false, 
  tooltipText = 'Verified Member',
  sx = {} 
}) => {
  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <VerifiedBadgeContainer size={size} sx={sx}>
        <VerifiedIcon animate={animate} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          {/* Hexagonal Shield Background */}
          <path
            d="M12 2L3 7V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V7L12 2Z"
            fill="#22C55E"
            stroke="#16A34A"
            strokeWidth="0.5"
          />
          
          {/* Inner Shield Highlight */}
          <path
            d="M12 3.5L5 7.5V11C5 15.42 7.96 19.58 12 20.93C16.04 19.58 19 15.42 19 11V7.5L12 3.5Z"
            fill="url(#shieldGradient)"
            opacity="0.8"
          />
          
          {/* Checkmark */}
          <path
            d="M9 12L11 14L15 9"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          
          {/* Gradient Definition */}
          <defs>
            <linearGradient id="shieldGradient" x1="12" y1="3.5" x2="12" y2="20.93" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#22C55E" />
              <stop offset="100%" stopColor="#16A34A" />
            </linearGradient>
          </defs>
        </VerifiedIcon>
      </VerifiedBadgeContainer>
    </Tooltip>
  );
};

export default VerifiedBadge;
